// import { Icon } from '@nile/components';
import { Link } from '@superops/ui-design-system';
import React from 'react';
import { SVGIcon } from '@superops/super-icons';
import { COMMON_CONSTANTS } from '@nile/components';

const ticketNavigations = [
  {
    mainTitle: 'FREQUENTLY USED',
    items: [
      {
        url: '/ticket/myopentasks',
        label: 'My open taks'
      },
      {
        url: '/ticket/myoverviewtickets',
        label: 'My Over due tickets'
      },
      {
        url: '/ticket/pendingforapproval',
        label: 'Pending for approval'
      }
    ]
  },
  {
    mainTitle: 'DEFAULT VIEWS',
    items: [
      {
        url: '/ticket/highpriorityincidenttickets',
        label: 'High priority incident tickets'
      },
      {
        url: '/ticket/highpriorityservicetickets',
        label: 'High priority service tickets'
      },
      {
        url: '/ticket/mygrouptickets',
        label: 'My group tickets'
      }
    ]
  },
  {
    mainTitle: 'TICKET VIEWS',
    actions: (
      <Link removeUnderline url={'#/actions/ticket/view/new'}>
        <SVGIcon name="CirclePlus" />
      </Link>
    ),
    items: [
      {
        url: '/ticket/myopentickets',
        label: 'My High priority and open tickets'
      },
      {
        url: '/ticket/mypriorityservicetickets',
        label: 'My High priority and service tickets'
      }
    ]
  }
];

const defaultColumns = {
  ticketId: {
    Header: 'Ticket ID',
    accessor: 'displayId',
    type: 'Priority',
    className: 'sticky',
    disableResizing: true
  },
  subject: {
    Header: 'Subject',
    accessor: 'subject',
    type: 'HTML',
    className: 'sticky',
    disableResizing: true
  },
  requester: {
    Header: 'Requester',
    accessor: 'requester',
    type: 'Text',
    className: 'sticky',
    disableResizing: false
  },
  priority: {
    Header: 'Priority',
    accessor: 'priority',
    type: 'Text',
    className: 'sticky',
    disableResizing: false
  },
  resolutiontimer: {
    Header: 'Resolution Timer',
    accessor: 'resolutiontimer',
    type: 'Timer',
    className: 'sticky',
    disableResizing: false
  },
  status: {
    Header: 'Status',
    accessor: 'status',
    type: 'Status',
    disableResizing: false
  },
  assignee: {
    Header: 'Assignee',
    accessor: 'assignee',
    type: 'Text',
    disableResizing: false
  },
  client: {
    Header: 'Client',
    accessor: 'client',
    subAccessor: 'name',
    type: 'Text',
    disableResizing: false
  },
  tasks: {
    Header: 'Tasks',
    accessor: 'tasks',
    type: 'Step',
    disableResizing: false
  }
};

const PROPERTY_ORDER = {
  SLA: ['firstResponseTimer', 'resolutionTimer'],
  TICKET: [
    'status',
    'impact',
    'urgency',
    'priority',
    'category',
    'subcategory',
    'cause',
    'subcause',
    'sop',
    'resolutionCode'
  ],
  TECHNICIAN: ['techGroup', 'technician', 'followers'],
  CLIENT: ['client', 'site', 'requester', 'additionalRequester']
};

const NEW_TICKET_PROPERTY_ORDER = {
  SLA: [],
  TICKET: [
    'status',
    'impact',
    'urgency',
    'priority',
    'category',
    'subcategory',
    'cause',
    'subcause'
  ],
  TECHNICIAN: ['techGroup', 'technician'],
  CLIENT: []
};

const ERROR_MAP = {
  0: 'Error occured',
  2005: 'Tickets from different clients cannot be merged'
};

const TICKET_VIEW_IDS = {
  trash: '9',
  spam: '8'
};

const TICKET_SCHEMA = {
  id: 'ID',
  ticketId: 'ID',
  subject: 'String',
  client: 'ClientInput',
  requester: 'UserInput',
  techGroup: 'GroupInput',
  technician: 'UserInput',
  status: 'String',
  priority: 'String',
  impact: 'String',
  urgency: 'String',
  category: 'String',
  subcategory: 'String',
  cause: 'String',
  subcause: 'String',
  resolutionCode: 'String',
  ticketType: 'String',
  customFields: 'JSON',
  sop: 'StandardOperatingProcedureInput',
  addAdditionalRequester: '[UserInput]',
  deleteAdditionalRequester: '[UserInput]',
  addFollowers: '[UserInput]',
  deleteFollowers: '[UserInput]',
  site: 'SiteInput',
  department: 'BusinessFunctionIdentifierInput',
  contract: 'ContractIdentifierInput'
};

const defaultPersonalization = (isMSPProduct, isTechnicianPortal) => {
  const defaultColumnsList = [
    {
      name: 'prioritizedId',
      label: 'Prioritized Id',
      attributePlaceHolder: {
        key: 'attribute.prioritizedId',
        label: 'Prioritized Id'
      },
      isIdentifier: false,
      isKeyAttribute: false,
      dataType: 'string',
      entity: null,
      entityType: null,
      uiDataType: 'read_only',
      conditionPlaceHolders: null,
      isCustom: false,
      nestedTo: null,
      showToClient: true,
      id: 'prioritizedId'
    },
    {
      name: 'subject',
      label: 'Subject',
      attributePlaceHolder: { key: 'attribute.subject', label: 'Subject' },
      isIdentifier: false,
      isKeyAttribute: true,
      dataType: 'string',
      entity: null,
      entityType: null,
      uiDataType: 'text',
      conditionPlaceHolders: null,
      isCustom: false,
      nestedTo: null,
      showToClient: true,
      id: 'subject'
    },
    {
      name: 'status',
      label: 'Status',
      attributePlaceHolder: { key: 'common.status', label: 'Status' },
      isIdentifier: false,
      isKeyAttribute: false,
      dataType: 'string',
      entity: null,
      entityType: null,
      uiDataType: 'select',
      conditionPlaceHolders: null,
      isCustom: false,
      nestedTo: null,
      showToClient: true,
      id: 'status'
    },
    {
      name: 'requester',
      label: 'Requester',
      autoComplete: true,
      attributePlaceHolder: { key: 'entity.requester', label: 'Requester' },
      isIdentifier: false,
      isKeyAttribute: false,
      dataType: 'entity',
      entity: 'Requester',
      entityType: 'REFER_ENTITY',
      uiDataType: 'select',
      conditionPlaceHolders: [
        { key: 'placeholder.is.loggedin.user', label: 'Is Logged in User' }
      ],
      isCustom: false,
      nestedTo: null,
      showToClient: true,
      id: 'requester'
    },
    {
      name: 'technician',
      label: 'Technician',
      autoComplete: true,
      attributePlaceHolder: { key: 'entity.technician', label: 'Technician' },
      isIdentifier: false,
      isKeyAttribute: false,
      dataType: 'entity',
      entity: 'Technician',
      entityType: 'REFER_ENTITY',
      uiDataType: 'select',
      conditionPlaceHolders: [
        { key: 'placeholder.is.loggedin.user', label: 'Is Logged in User' }
      ],
      isCustom: false,
      nestedTo: null,
      showToClient: true,
      id: 'technician'
    },
    {
      name: 'createdTime',
      label: 'Created time',
      attributePlaceHolder: {
        key: 'entityAttribute.ticket.createdTime',
        label: 'entityAttribute.ticket.createdTime'
      },
      filterType: null,
      isIdentifier: false,
      isKeyAttribute: false,
      dataType: 'datetime',
      entity: null,
      uiDataType: 'read_only',
      conditionPlaceHolders: null,
      isCustom: false,
      nestedTo: null,
      showToClient: true,
      autoComplete: false,
      useInMetric: false,
      metricFunctions: null,
      metricUnits: null,
      labelInMetric: null,
      useInGroupBy: true,
      useInCondition: true,
      conditionPlaceHoldersMeta: null
    }
  ];
  if (isTechnicianPortal) {
    defaultColumnsList.push({
      name: 'resolutionTimer',
      label: 'Resolution Timer',
      attributePlaceHolder: {
        key: 'attribute.resolutionTimer',
        label: 'Resolution Timer'
      },
      isIdentifier: false,
      isKeyAttribute: false,
      dataType: null,
      entity: null,
      entityType: null,
      uiDataType: 'read_only',
      conditionPlaceHolders: null,
      isCustom: false,
      nestedTo: null,
      showToClient: true,
      id: 'resolutionTimer'
    });
  }

  if (isMSPProduct) {
    defaultColumnsList.push({
      name: 'client',
      label: 'Client',
      attributePlaceHolder: { key: 'entity.client', label: 'Client' },
      isIdentifier: false,
      isKeyAttribute: false,
      dataType: 'entity',
      entity: 'Client',
      entityType: 'REFER_ENTITY',
      uiDataType: 'select',
      conditionPlaceHolders: [
        { key: 'placeholder.is.loggedin.user', label: 'Is Logged in User' }
      ],
      isCustom: false,
      nestedTo: null,
      showToClient: true,
      id: 'client',
      productToCheck: COMMON_CONSTANTS.MSP
    });
  }
  return defaultColumnsList;
};

const getAllTicketFields = () => {
  return [
    'ticketId',
    'firstResponseDueTime',
    'firstResponseDueTimeLeft',
    'firstResponseTime',
    'firstResponseViolated',
    'resolutionDueTime',
    'resolutionPauseTime',
    'resolutionDueTimeLeft',
    'resolutionTime',
    'status',
    'impact',
    'urgency',
    'priority',
    'category',
    'subcategory',
    'cause',
    'subcause',
    'resolutionCode',
    'displayId',
    'subject',
    'customFields',
    {
      techGroup: ['groupId', 'name']
    },
    {
      technician: ['userId', 'email']
    },
    {
      site: ['name', 'id']
    }
  ];
};

const REQUIRED_TICKET_FIELDS = ['status', 'client', 'requester'];

const sortActionsTicketKanbanMap = [
  {
    label: 'kanbanview.actionlist.createdTime',
    value: 'createdTime'
  },
  {
    label: 'kanbanview.actionlist.ticketID',
    value: 'displayId'
  },
  {
    label: 'kanbanview.actionlist.ticketClient',
    value: 'client.name'
  },
  {
    label: 'kanbanview.actionlist.ticketPriority',
    value: 'priority'
  },
  {
    label: 'common.status',
    value: 'status'
  },
  {
    label: 'kanbanview.actionlist.ticketRequester',
    value: 'requester.name'
  },
  {
    label: 'kanbanview.actionlist.ticketSubject',
    value: 'subject'
  },
  {
    label: 'kanbanview.actionlist.ticketResolutionTime',
    value: 'resolutionDueTime'
  },
  {
    label: 'kanbanview.actionlist.ticketTechnician',
    value: 'technician.name'
  }
];

const TicketKanbanTabs = [
  {
    id: 'status',
    label: 'common.status',
    qa: 'kanbanStatus'
  },
  {
    id: 'priority',
    label: 'ticket.kanban.priority.nav',
    qa: 'kanbanPriority'
  },
  {
    id: 'group',
    label: 'ticket.kanban.group.nav',
    qa: 'kanbanGroup'
  },
  {
    id: 'technician',
    label: 'ticket.kanban.technician.nav',
    qa: 'kanbanTechnician'
  }
];

const TaskKanbanTabs = [
  {
    id: 'status',
    label: 'common.status',
    qa: 'kanbanStatus'
  },
  {
    id: 'group',
    label: 'ticket.kanban.group.nav',
    qa: 'kanbanGroup'
  },
  {
    id: 'technician',
    label: 'ticket.kanban.technician.nav',
    qa: 'kanbanTechnician'
  },
  {
    id: 'priority',
    label: 'ticket.kanban.priority.nav',
    qa: 'kanbanPriority'
  }
];

const entityShareType = {
  ALL: 'ALL',
  SPECIFIC: 'SPECIFIC'
};

const shareTypeList = {
  ALL: 'alltechnicians',
  SPECIFIC_USER: 'specifictechnicians',
  SPECIFIC_GROUP: 'techniciangroups'
};

const shareViewRadioOptions = [
  {
    id: shareTypeList.ALL,
    labelKey: 'common.all.technician'
  },
  {
    id: shareTypeList.SPECIFIC_USER,
    labelKey: 'common.specific.technicians'
  },
  {
    id: shareTypeList.SPECIFIC_GROUP,
    labelKey: 'dromo.title.technicianGroups'
  }
];

export {
  ticketNavigations,
  defaultColumns,
  PROPERTY_ORDER,
  ERROR_MAP,
  TICKET_SCHEMA,
  defaultPersonalization,
  getAllTicketFields,
  NEW_TICKET_PROPERTY_ORDER,
  TICKET_VIEW_IDS,
  REQUIRED_TICKET_FIELDS,
  sortActionsTicketKanbanMap,
  TicketKanbanTabs,
  TaskKanbanTabs,
  entityShareType,
  shareTypeList,
  shareViewRadioOptions
};
